import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { InventoryItem, RoomPhoto, Room } from '../types';
import { HTMLInventoryPDFService, PropertyInventoryData } from '../services/htmlInventoryPDFService';
import logo from '../assets/logo.png';
import type { Signature } from '../types';
import type { PropertyDetails } from '../components/PropertyDetailsForm';
import type { KeyEntry } from '../components/KeyEntryItem';
import { conditionDescriptions, cleanlinessDescriptions } from '../constants/inventoryConstants';

// Define the Room structure expected by this utility
interface PdfRoom {
  name: string;
  items: InventoryItem[];
  // Mid-term specific fields
  rating?: 1 | 2 | 3 | 4 | 5;
  cleanliness?: 'professionally cleaned' | 'good domestic standard' | 'average domestic standard' | 'poor domestic standard' | 'not cleaned' | '';
  // Add other properties like signatures if your PDF generation logic uses them for rooms
}

// Helper function type (adjust if needed)
// type jsPDFWithAutoTable = jsPDF & { autoTable: (options: any) => jsPDF };

// Define the props interface for the generator function
interface GeneratePdfProps {
  reportType: 'checkIn' | 'checkOut' | 'midTerm';
  propertyDetails: PropertyDetails;
  rooms: PdfRoom[]; // Use the local PdfRoom type
  certificates: { gas: { date: string; number: string; }; electrical: { date: string; number: string; }; };
  exportType?: 'jspdf' | 'html'; // Optional parameter to choose export method
  keys: KeyEntry[];
  meterReadings: { gas: any; electricity: any; water: any; }; // Use specific types if available
  signatures: Signature;
  options?: { 
    logoDataUrl?: string; 
    headerColor?: string; 
    formatChoice?: 'pdf' | 'html' | 'both';
    logo?: string;
    companyName?: string;
    companyWebsite?: string;
    companyPhone?: string;
    companyEmail?: string;
    companyAddress?: string;
  }; // Customization options
  // Add other dependencies if needed
}

// Define an interface for the didDrawPage callback data
interface AutoTableHookData {
  pageNumber: number;
  settings: any; // You can be more specific if you know the settings structure
  doc: jsPDF;
  cursor?: { x: number; y: number };
  table: any; // You can be more specific with table type
  // Add other properties you might use from the 'data' object
}

// Extend jsPDF with AutoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

const FONT_SIZE_NORMAL = 10;
const FONT_SIZE_SMALL = 8;
const LINE_HEIGHT = 7;
const MARGIN = 15;
let currentY = MARGIN; // Keep track of Y position on the current page

// Helper to add text and manage Y position
function addText(doc: jsPDF, text: string | string[], x: number, yAlign: 'top' | 'bottom' | number, size: number = FONT_SIZE_NORMAL, options?: any) {
  doc.setFontSize(size);
  let textY = yAlign === 'top' ? currentY : (yAlign === 'bottom' ? doc.internal.pageSize.height - MARGIN : yAlign);
  
  const lines = Array.isArray(text) ? text : doc.splitTextToSize(text, doc.internal.pageSize.width - MARGIN * 2 - x);
  
  if (textY + (lines.length * (size / 2.5)) > doc.internal.pageSize.height - MARGIN) { // Approximate check for page overflow
    doc.addPage();
    currentY = MARGIN;
    textY = currentY;
  }
  
  doc.text(lines, x, textY, options);
  currentY = textY + (lines.length * (size / 2.5)) + 2; // Update currentY
  return currentY;
}

// Helper to add a new page if content exceeds current page
function checkAndAddPage(doc: jsPDF, yPosition: number, contentHeight: number = 20): number {
  if (yPosition + contentHeight > doc.internal.pageSize.height - MARGIN) {
    doc.addPage();
    currentY = MARGIN;
    return MARGIN;
  }
  return yPosition;
}

// Note: Removed aspect ratio preservation functions since all images will be uniform size

// Helper function to convert URL to base64 for PDF embedding
const urlToBase64 = async (url: string): Promise<string> => {
  try {
    // If it's already a base64 data URL, return as-is
    if (url.startsWith('data:')) {
      return url;
    }
    
    // Fetch the image from URL and convert to base64
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting URL to base64:', error);
    throw error;
  }
};

// Helper function to convert jsPDF data format to HTML format
const convertToHTMLFormat = (props: GeneratePdfProps): PropertyInventoryData => {
  // DEBUG: Log property details to verify inspection date
  console.log('📋 PDF Generator - Property Details:', {
    inspectionDate: props.propertyDetails.inspectionDate,
    checkInDate: props.propertyDetails.checkInDate,
    checkOutDate: props.propertyDetails.checkOutDate,
    address: props.propertyDetails.address
  });
  
  // DEBUG: Log keys, certificates, and meter readings with photos
  console.log('📋 PDF Generator - Keys with Photos:', props.keys);
  console.log('📋 PDF Generator - Certificates with Photos:', props.certificates);
  console.log('📋 PDF Generator - Meter Readings with Photos:', props.meterReadings);
  
  // DEBUG: Log incoming room data
  console.log('📋 PDF Generator - Incoming Rooms:', props.rooms.map((r, i) => ({
    index: i,
    name: r.name,
    rating: r.rating,
    cleanliness: r.cleanliness,
    itemCount: r.items?.length || 0
  })));
  
  return {
    rooms: props.rooms.map(room => ({
      id: room.name,
      name: room.name,
      items: room.items,
      // Mid-term specific fields
      rating: (room as any).rating,
      cleanliness: (room as any).cleanliness
    })),
    propertyDetails: {
      address: props.propertyDetails.address || '',
      reportType: props.reportType,
      reportDate: props.propertyDetails.inspectionDate || new Date().toLocaleDateString(),
      propertyType: props.propertyDetails.propertyType,
      agent: props.propertyDetails.inspectedBy,
      tenantName: props.propertyDetails.tenantNames?.join(', ') || '',
      landlordName: props.propertyDetails.landlordName,
      checkInDate: props.propertyDetails.checkInDate,
      checkOutDate: props.propertyDetails.checkOutDate,
      bedrooms: props.propertyDetails.bedrooms,
      cleanliness: props.propertyDetails.cleanliness
    },
    // Pass certificates with photos directly (structure already matches)
    certificates: {
      gas: {
        date: props.certificates.gas.date || '',
        number: props.certificates.gas.number || '',
        photos: (props.certificates.gas as any).photos || []
      },
      electrical: {
        date: props.certificates.electrical.date || '',
        number: props.certificates.electrical.number || '',
        photos: (props.certificates.electrical as any).photos || []
      }
    },
    keys: props.keys?.map(key => ({
      handedOver: key.handedOver || 'N/A',
      type: key.type || 'N/A',
      quantity: key.quantity?.toString() || '1',
      photos: (key as any).photos || []
    })) || [],
    meterReadings: {
      gas: props.meterReadings.gas ? {
        location: props.meterReadings.gas.location || '',
        serialNo: props.meterReadings.gas.serialNo || '',
        reading: props.meterReadings.gas.reading?.toString() || '',
        photos: (props.meterReadings.gas as any).photos || []
      } : undefined,
      electricity: props.meterReadings.electricity ? {
        location: props.meterReadings.electricity.location || '',
        serialNo: props.meterReadings.electricity.serialNo || '',
        reading: props.meterReadings.electricity.reading?.toString() || '',
        photos: (props.meterReadings.electricity as any).photos || []
      } : undefined,
      water: props.meterReadings.water ? {
        location: props.meterReadings.water.location || '',
        serialNo: props.meterReadings.water.serialNo || '',
        reading: props.meterReadings.water.reading?.toString() || '',
        photos: (props.meterReadings.water as any).photos || []
      } : undefined
    },
    signatures: {
      landlord: (props.signatures as any)?.landlord,
      agent: (props.signatures as any)?.agent,
      customer: (props.signatures as any)?.customer,
      assessor: (props.signatures as any)?.assessor,
      assessorDate: (props.signatures as any)?.assessorDate,
      customerDate: (props.signatures as any)?.customerDate
    },
    glossaryTerms: {
      ...conditionDescriptions,
      ...cleanlinessDescriptions
    }
  };
};

export const generateInventoryPDF = async ({
  reportType,
  propertyDetails,
  rooms,
  certificates,
  keys,
  meterReadings,
  signatures,
  exportType = 'jspdf', // Default to existing jsPDF behavior
  options = {}
}: GeneratePdfProps): Promise<void> => {
  try {
    const {
      logo,
      headerColor = '#f59e0b',
      logoDataUrl,
      companyName = '',
      companyWebsite = '',
      companyPhone = '',
      companyEmail = '',
      companyAddress = ''
    } = options;

    // Route to appropriate export method
    if (exportType === 'html') {
      const htmlData = convertToHTMLFormat({
        reportType,
        propertyDetails,
        rooms,
        certificates,
        keys,
        meterReadings,
        signatures,
        exportType
      });
      
      // Generate filename in format: [Check-in] 13 Skiffington Close, London, SW2 3UL
      const reportTypeText = reportType === 'checkIn' ? 'Check-in' : reportType === 'midTerm' ? 'Mid-Term Report' : 'Check-out';
      const address = propertyDetails?.address || 'Property';
      const cleanFilename = `[${reportTypeText}] ${address}`
        .replace(/[<>:"/\\|?*]/g, '') // Remove invalid file system characters
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      
      await HTMLInventoryPDFService.exportInventoryToPDF(
        htmlData,
        `${cleanFilename}.pdf`,
        options || {},
        options?.formatChoice || 'html'
      );
      return;
    }

    // Original jsPDF implementation
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    let yPos = 20; // This will be reset by addHeader
    let currentPage = 1;
    const margin = 15;
    const pageWidth = 210 - (margin * 2);
    const pageHeight = 297;
    const footerHeight = 30; // Approximate height for the footer region

    const titleSize = 16;
    const headingSize = 14;
    const subheadingSize = 12;
    const normalSize = 10;
    const smallSize = 8;
    const primaryColor = '#009688'; 
    const secondaryColor = '#00796b'; 
    const textColor = '#333333'; 
    const lightGray = '#f5f5f5'; 

    const contentStartY = margin + 25 + 10; 

    let totalPages = 1; // Will be calculated at the end

    const addHeader = (pageNum?: number, totalPagesNum?: number) => {
      const headerBottomMargin = 10;
      try {
        const logoWidth = 40;
        const logoHeight = 15;
        // Use custom logo or default imported logo (ensure always string)
        const logoToUse: string = (logoDataUrl as string) || (logo as string);
        pdf.addImage(logoToUse, 'PNG', margin, margin, logoWidth, logoHeight);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(titleSize);
        pdf.setTextColor(secondaryColor);
        const reportTitleText = `Property Inventory Report (${reportType === 'checkIn' ? 'Check-in' : 'Check-out'})`;
        pdf.text(reportTitleText, margin + logoWidth + 5, margin + 8);

        // Only show company info in header if provided
        if (companyName || companyWebsite) {
          let headerY = margin + 15;
          if (companyName) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(normalSize);
            pdf.text(companyName, margin + logoWidth + 5, headerY);
            headerY += 5;
          }
          if (companyWebsite) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(smallSize);
            pdf.text(companyWebsite, margin + logoWidth + 5, headerY);
          }
        }
        
        // Add page number to header if provided
        if (pageNum && totalPagesNum) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(smallSize);
          pdf.setTextColor(secondaryColor);
          pdf.text(`Page ${pageNum} of ${totalPagesNum}`, 210 - margin, margin + 8, { align: 'right' });
        }

        pdf.setDrawColor(primaryColor);
        pdf.setLineWidth(0.5);
        pdf.line(margin, margin + 25, 210 - margin, margin + 25);
        
        yPos = contentStartY; // Reset yPos for new page content
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(titleSize);
        pdf.setTextColor(secondaryColor);
        const reportTitleText = `Property Inventory Report (${reportType === 'checkIn' ? 'Check-in' : 'Check-out'})`;
        pdf.text(reportTitleText, margin, margin + 10);
        pdf.setDrawColor(primaryColor);
        pdf.setLineWidth(0.5);
        pdf.line(margin, margin + 15, 210 - margin, margin + 15);
        yPos = margin + 15 + headerBottomMargin; 
      }
    };

    const addFooter = () => {
      // Footer now only contains minimal content - page numbers moved to header
      // Keeping footer function for potential future use but removing all content
    };

    const checkForNewPage = (currentYPos: number, requiredSpace: number): number => {
       if (currentYPos + requiredSpace > pageHeight - footerHeight) {
         // Footer will be added globally at the end or by addPage if full
         pdf.addPage();
         currentPage++;
         addHeader();
         return contentStartY; // yPos is reset by addHeader
       }
       return currentYPos;
     };

    const addSectionSpacing = (amount = 15) => { // This helper might be less needed with explicit page breaks
      yPos += amount;
      yPos = checkForNewPage(yPos, 0); // Check if spacing pushed to new page
    };
    
    const drawPhotoItemDetails = (item: InventoryItem, startY: number): number => {
      let currentY = startY;
      const itemBlockPadding = 3; // Reduced padding
      const detailsBlockInnerWidth = pageWidth - (itemBlockPadding * 2);
      
      // Create compact single line format: "Category | Condition: Good | Notes: Text"
      const itemTitle = item.category;
      const conditionText = `Condition: ${item.condition || 'N/A'}`;
      const notesText = item.notes ? `Notes: ${item.notes}` : 'Notes: None';
      const compactText = `${itemTitle} | ${conditionText} | ${notesText}`;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(smallSize);
      const splitText = pdf.splitTextToSize(compactText, detailsBlockInnerWidth);
      const detailsBlockHeight = (splitText.length * 3.5) + (itemBlockPadding * 2); // Much more compact
      
      pdf.setFillColor(lightGray);
      pdf.setDrawColor(primaryColor);
      pdf.setLineWidth(0.2);
      pdf.roundedRect(margin, currentY, pageWidth, detailsBlockHeight, 2, 2, 'FD');
      
      pdf.setTextColor(textColor);
      pdf.text(splitText, margin + itemBlockPadding, currentY + itemBlockPadding + 3);
      return currentY + detailsBlockHeight;
   };

    // --- Start PDF Content ---
    // Page 1: Property Details & Description
    addHeader(); 
    
    // Pre-calculate address wrapping to determine box height
    const col1X = margin + 5;
    const col2X = margin + pageWidth / 2 + 5;
    const addressText = propertyDetails.address || 'N/A';
    const maxAddressWidth = col2X - (col1X + 25) - 10; // Available width with padding
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(normalSize);
    const addressLines = pdf.splitTextToSize(addressText, maxAddressWidth);
    const addressLineCount = addressLines.length;
    const addressExtraSpace = Math.max(0, (addressLineCount - 1) * 5); // 5mm per extra line
    
    // Draw Property Details box with dynamic height
    const propertyDetailsBoxHeight = 60 + addressExtraSpace;
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, propertyDetailsBoxHeight, 3, 3, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Property Details', margin + 5, yPos + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(normalSize);
    
    // Render address with wrapping
    pdf.setFont('helvetica', 'bold');
    pdf.text('Address:', col1X, yPos + 20);
    pdf.setFont('helvetica', 'normal');
    pdf.text(addressLines, col1X + 25, yPos + 20);
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('Inspection Date:', col1X, yPos + 30 + addressExtraSpace);
    pdf.setFont('helvetica', 'normal');
    pdf.text(propertyDetails.inspectionDate || 'N/A', col1X + 35, yPos + 30 + addressExtraSpace);
    // Show only relevant date field based on report type
    if (reportType === 'checkIn') {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Check-in Date:', col1X, yPos + 40 + addressExtraSpace);
      pdf.setFont('helvetica', 'normal');
      pdf.text(propertyDetails.checkInDate || 'N/A', col1X + 35, yPos + 40 + addressExtraSpace);
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Check-out Date:', col1X, yPos + 40 + addressExtraSpace);
      pdf.setFont('helvetica', 'normal');
      pdf.text(propertyDetails.checkOutDate || 'N/A', col1X + 35, yPos + 40 + addressExtraSpace);
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text('Inspected By:', col2X, yPos + 20);
    pdf.setFont('helvetica', 'normal');
    pdf.text(propertyDetails.inspectedBy || 'N/A', col2X + 30, yPos + 20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Landlord:', col2X, yPos + 30);
    pdf.setFont('helvetica', 'normal');
    pdf.text(propertyDetails.landlordName || 'N/A', col2X + 30, yPos + 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Tenant(s):', col2X, yPos + 40);
    pdf.setFont('helvetica', 'normal');
    const tenants = propertyDetails.tenantNames?.join(', ') || 'N/A';
    pdf.text(tenants, col2X + 30, yPos + 40);
    yPos += 70 + addressExtraSpace; // Account for multi-line address


    yPos = checkForNewPage(yPos, 70); // Check if description block fits
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, 60, 3, 3, 'FD'); 
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Property Description', margin + 5, yPos + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(normalSize);
    let descriptionInternalY = yPos + 18;
    if (propertyDetails.propertyType) {
        pdf.setFont('helvetica', 'bold'); pdf.text('Property Type:', margin + 5, descriptionInternalY);
        pdf.setFont('helvetica', 'normal'); pdf.text(propertyDetails.propertyType.charAt(0).toUpperCase() + propertyDetails.propertyType.slice(1), margin + 40, descriptionInternalY);
        descriptionInternalY += 8;
    }
    if (propertyDetails.bedrooms) {
        pdf.setFont('helvetica', 'bold'); pdf.text('Bedrooms:', margin + 5, descriptionInternalY);
        pdf.setFont('helvetica', 'normal'); pdf.text(propertyDetails.bedrooms, margin + 40, descriptionInternalY);
        descriptionInternalY += 8;
    }
    if (propertyDetails.cleanliness) {
        pdf.setFont('helvetica', 'bold'); pdf.text('Cleanliness:', margin + 5, descriptionInternalY);
        pdf.setFont('helvetica', 'normal'); pdf.text(propertyDetails.cleanliness.charAt(0).toUpperCase() + propertyDetails.cleanliness.slice(1), margin + 40, descriptionInternalY);
        descriptionInternalY += 8;
    }
    if (propertyDetails.propertyDescription) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Additional Description:', margin + 5, descriptionInternalY);
        pdf.setFont('helvetica', 'normal');
        const splitDescription = pdf.splitTextToSize(propertyDetails.propertyDescription, pageWidth - 10);
        pdf.text(splitDescription, margin + 5, descriptionInternalY + 8);
        descriptionInternalY += (splitDescription.length * 5) + 8; 
    }
    const descriptionBlockActualHeight = Math.max(60, descriptionInternalY - yPos + 5);
    yPos += descriptionBlockActualHeight;

    // Page 2: Glossary of Terms
    pdf.addPage(); currentPage++; addHeader(); 
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(headingSize); 
    pdf.setTextColor(secondaryColor);
    pdf.text('Glossary of Terms', margin, yPos);
    pdf.setDrawColor(primaryColor); 
    pdf.setLineWidth(0.5);
    // Adjust underline to fit text width
    const glossaryTitleWidth = pdf.getTextWidth('Glossary of Terms');
    pdf.line(margin, yPos + 5, margin + glossaryTitleWidth, yPos + 5); 
    yPos += 15;

    const colWidthGlossary = (pageWidth - 10) / 2; 
    const leftColXGlossary = margin;
    const rightColXGlossary = margin + colWidthGlossary + 10; 

    const drawGlossaryCategory = (title: string, descriptions: Record<string, string>) => {
      // Category Title Block
      const categoryTitleBlockHeight = 15; // Height for the title block
      const categoryTitlePaddingY = 5; // Padding below category title block
      yPos = checkForNewPage(yPos, categoryTitleBlockHeight + categoryTitlePaddingY); 
      
      pdf.setFillColor(lightGray);
      pdf.setDrawColor(lightGray); // Border same as fill
      pdf.setLineWidth(0.1); 
      pdf.roundedRect(margin, yPos, pageWidth, categoryTitleBlockHeight - categoryTitlePaddingY , 3, 3, 'FD');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(subheadingSize - 1); // Slightly smaller for category title
      pdf.setTextColor(secondaryColor); 
      // Try to vertically center text in the category block
      const textMetricsCategory = pdf.getTextDimensions(title, { fontSize: subheadingSize -1 });
      pdf.text(title, margin + 5, yPos + ((categoryTitleBlockHeight - categoryTitlePaddingY) / 2) + (textMetricsCategory.h / 2) - 1.5);
      yPos += categoryTitleBlockHeight; // Advance yPos by full block height (incl. its bottom padding)

      const terms = Object.keys(descriptions);
      const numTerms = terms.length;
      const itemsInLeftColCount = Math.ceil(numTerms / 2);
      
      let leftColCurrentY = yPos;
      let rightColCurrentY = yPos;

      const termBlockInnerPadding = 4; 
      const termVerticalSpacing = 5; // Spacing between term blocks

      for (let i = 0; i < numTerms; i++) {
        const term = terms[i];
        const descriptionText = descriptions[term];
        let itemX;
        let currentYForThisItemColumn; 

        const isLeftColumnItem = i < itemsInLeftColCount;

        if (isLeftColumnItem) {
          itemX = leftColXGlossary;
          currentYForThisItemColumn = leftColCurrentY;
        } else {
          itemX = rightColXGlossary;
          currentYForThisItemColumn = rightColCurrentY;
        }

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT_SIZE_NORMAL);
        const termLines = pdf.splitTextToSize(term, colWidthGlossary - (termBlockInnerPadding * 2));
        const termHeight = termLines.length * (FONT_SIZE_NORMAL / 2.5); // Approximation

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(FONT_SIZE_SMALL);
        const descLines = pdf.splitTextToSize(descriptionText, colWidthGlossary - (termBlockInnerPadding * 2));
        const descHeight = descLines.length * (FONT_SIZE_SMALL / 2.5); // Approximation
        
        const blockContentHeight = termHeight + 1.5 + descHeight; // term + small space + description
        const currentBlockTotalHeight = blockContentHeight + (termBlockInnerPadding * 2);

        if (currentYForThisItemColumn + currentBlockTotalHeight > pageHeight - footerHeight) {
            pdf.addPage(); currentPage++; addHeader(); 
            
            leftColCurrentY = yPos; 
            rightColCurrentY = yPos;
            
            // Re-draw category title if it splits across pages
            pdf.setFillColor(lightGray);
            pdf.setDrawColor(lightGray);
            pdf.setLineWidth(0.1);
            pdf.roundedRect(margin, yPos, pageWidth, categoryTitleBlockHeight - categoryTitlePaddingY, 3, 3, 'FD');
            const continuedTitle = `${title} (continued)`;
            const textMetricsCont = pdf.getTextDimensions(continuedTitle, { fontSize: subheadingSize - 1});
            pdf.setFont('helvetica', 'bold'); pdf.setFontSize(subheadingSize - 1); pdf.setTextColor(secondaryColor);
            pdf.text(continuedTitle, margin + 5, yPos + ((categoryTitleBlockHeight - categoryTitlePaddingY) / 2) + (textMetricsCont.h / 2) - 1.5);
            yPos += categoryTitleBlockHeight;

            leftColCurrentY = yPos;
            rightColCurrentY = yPos;

            currentYForThisItemColumn = isLeftColumnItem ? leftColCurrentY : rightColCurrentY;
        }
        
        pdf.setFillColor(lightGray);
        pdf.setDrawColor(lightGray); 
        pdf.setLineWidth(0.1);
        pdf.roundedRect(itemX, currentYForThisItemColumn, colWidthGlossary, currentBlockTotalHeight, 2, 2, 'FD');

        let textYInBlock = currentYForThisItemColumn + termBlockInnerPadding;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT_SIZE_NORMAL); 
        pdf.setTextColor(secondaryColor); 
        pdf.text(termLines, itemX + termBlockInnerPadding, textYInBlock + (FONT_SIZE_NORMAL / 2.8)); // Y pos for baseline
        textYInBlock += termHeight + 1.5; 

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(FONT_SIZE_SMALL); 
        pdf.setTextColor(textColor); 
        pdf.text(descLines, itemX + termBlockInnerPadding, textYInBlock + (FONT_SIZE_SMALL / 2.8)); // Y pos for baseline
        
        if (isLeftColumnItem) {
          leftColCurrentY = currentYForThisItemColumn + currentBlockTotalHeight + termVerticalSpacing;
        } else {
          rightColCurrentY = currentYForThisItemColumn + currentBlockTotalHeight + termVerticalSpacing;
        }
      }
      yPos = Math.max(leftColCurrentY, rightColCurrentY) - termVerticalSpacing + 5; 
    };
    
    if (conditionDescriptions && Object.keys(conditionDescriptions).length > 0) {
      drawGlossaryCategory("Condition Descriptions", conditionDescriptions);
    }
    if (cleanlinessDescriptions && Object.keys(cleanlinessDescriptions).length > 0) {
      yPos = checkForNewPage(yPos, 25); // Check space for next category title
      // If a new page was added, yPos is already reset by addHeader
      drawGlossaryCategory("Cleanliness Descriptions", cleanlinessDescriptions);
    }

    // Page 3: Certificates, Keys, Meter Readings
    pdf.addPage(); currentPage++; addHeader(); 

    // --- Certificates Section ---
    const certificatesBlockHeight = 55; // Adjusted height
    yPos = checkForNewPage(yPos, certificatesBlockHeight); 
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, certificatesBlockHeight, 3, 3, 'FD'); 
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Certificates', margin + 5, yPos + 8);

    const certData = [];
    if (certificates) {
        if (certificates.gas) certData.push(['Gas Safety', certificates.gas.date || 'N/A', 'N/A', certificates.gas.number || 'N/A']);
        if (certificates.electrical) certData.push(['Electrical Safety', certificates.electrical.date || 'N/A', 'N/A', certificates.electrical.number || 'N/A']);
    }

    if (certData.length > 0) {
        pdf.autoTable({
            head: [['Type', 'Issue Date', 'Expiry Date', 'Reference']], 
            body: certData,
            startY: yPos + 15,
            theme: 'plain', // Changed from 'striped' to 'plain' for white rows
            styles: { cellPadding: 2, fontSize: normalSize - 1, textColor: textColor }, // Ensure body text color
            headStyles: { fillColor: secondaryColor, textColor: 255, fontStyle: 'bold', halign: 'left', fontSize: normalSize -1 },
            columnStyles: { 
                0: { halign: 'left'}, 
                1: { halign: 'left'}, 
                2: { halign: 'left'}, 
                3: { halign: 'left'} 
            },
            margin: { left: margin + 5, right: margin + 5 },
            tableWidth: pageWidth - 10,
            didDrawPage: (data: AutoTableHookData) => { 
                if(data.pageNumber > currentPage) { 
                    currentPage = data.pageNumber; 
                    addHeader(); 
                }
                // yPos update should ideally happen after the table if it doesn't span:
                // For now, rely on the global yPos update after this block
            }
        });
        yPos = (pdf as any).lastAutoTable.finalY > (yPos + certificatesBlockHeight) ? (pdf as any).lastAutoTable.finalY + 10 : yPos + certificatesBlockHeight + 10;
    } else { 
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(normalSize);
        pdf.setTextColor(textColor);
        pdf.text('No certificates applicable or recorded.', margin + 10, yPos + 25);
        yPos += certificatesBlockHeight + 10; 
    }

    // --- Keys Section ---
    const keysBlockHeight = 45; // Adjusted height
    yPos = checkForNewPage(yPos, keysBlockHeight);
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, keysBlockHeight, 3, 3, 'FD'); 
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Keys', margin + 5, yPos + 8);

    if (keys && keys.length > 0) {
        const keyData = keys.map(key => [key.handedOver || 'N/A', key.type || 'N/A', key.quantity || 'N/A']);
        pdf.autoTable({
            head: [['Keys Handed Over', 'Type of Key', 'Number of Keys']], 
            body: keyData,
            startY: yPos + 15,
            theme: 'plain', // Changed from 'striped'
            styles: { cellPadding: 2, fontSize: normalSize - 1, textColor: textColor },
            headStyles: { fillColor: secondaryColor, textColor: 255, fontStyle: 'bold', fontSize: normalSize -1 }, // General halign will be overridden by columnStyles
            columnStyles: { 
                0: { halign: 'left'}, 
                1: { halign: 'left'}, 
                2: { halign: 'center'} 
            },
            margin: { left: margin + 5, right: margin + 5 },
            tableWidth: pageWidth - 10,
            didDrawPage: (data: AutoTableHookData) => { 
                if(data.pageNumber > currentPage) { 
                    currentPage = data.pageNumber; 
                    addHeader();
                }
            }
        });
        yPos = (pdf as any).lastAutoTable.finalY > (yPos + keysBlockHeight) ? (pdf as any).lastAutoTable.finalY + 10 : yPos + keysBlockHeight + 10;
    } else { 
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(normalSize);
        pdf.setTextColor(textColor);
        pdf.text('No key information recorded.', margin + 10, yPos + 25);
        yPos += keysBlockHeight + 10; 
    }
    
    // --- Meter Readings Section ---
    const meterReadingsBlockHeight = 60; // Adjusted height
    yPos = checkForNewPage(yPos, meterReadingsBlockHeight);
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, meterReadingsBlockHeight, 3, 3, 'FD'); 
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Meter Readings', margin + 5, yPos + 8);

    const meterData = [];
    if (meterReadings) {
        if (meterReadings.gas) meterData.push(['Gas', meterReadings.gas.location || 'N/A', meterReadings.gas.serialNo || 'N/A', meterReadings.gas.reading || 'N/A']);
        if (meterReadings.electricity) meterData.push(['Electricity', meterReadings.electricity.location || 'N/A', meterReadings.electricity.serialNo || 'N/A', meterReadings.electricity.reading || 'N/A']);
        if (meterReadings.water) meterData.push(['Water', meterReadings.water.location || 'N/A', meterReadings.water.serialNo || 'N/A', meterReadings.water.reading || 'N/A']);
    }

    if (meterData.length > 0) {
        pdf.autoTable({
            head: [['Type', 'Location', 'Serial Number', 'Reading']], 
            body: meterData,
            startY: yPos + 15,
            theme: 'plain', // Changed from 'striped'
            styles: { cellPadding: 2, fontSize: normalSize - 1, textColor: textColor },
            headStyles: { fillColor: secondaryColor, textColor: 255, fontStyle: 'bold', halign: 'left', fontSize: normalSize -1 },
            columnStyles: { 
                0: { halign: 'left'}, 
                1: { halign: 'left'}, 
                2: { halign: 'left'}, 
                3: { halign: 'left'} 
            },
            margin: { left: margin + 5, right: margin + 5 },
            tableWidth: pageWidth - 10,
            didDrawPage: (data: AutoTableHookData) => { 
                if(data.pageNumber > currentPage) { 
                    currentPage = data.pageNumber; 
                    addHeader(); 
                }
            }
        });
        yPos = (pdf as any).lastAutoTable.finalY > (yPos + meterReadingsBlockHeight) ? (pdf as any).lastAutoTable.finalY + 10 : yPos + meterReadingsBlockHeight + 10;
    } else { 
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(normalSize);
        pdf.setTextColor(textColor);
        pdf.text('No meter readings recorded.', margin + 10, yPos + 25);
        yPos += meterReadingsBlockHeight + 10; 
    }

    // Room Inventory Sections (Each room starts on a new page)
    rooms.forEach((room) => {
        pdf.addPage(); currentPage++; addHeader(); 

        const roomHeaderHeight = 15;
        pdf.setFillColor(primaryColor); 
        pdf.setDrawColor(primaryColor);
        pdf.roundedRect(margin, yPos, pageWidth, 12, 2, 2, 'FD');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(headingSize); pdf.setTextColor(255, 255, 255);
        pdf.text(`Room: ${room.name}`, margin + 5, yPos + 8);
        yPos += roomHeaderHeight;

        if (room.items.length === 0) {
            yPos = checkForNewPage(yPos, 15);
            pdf.setFont('helvetica', 'italic'); pdf.setFontSize(normalSize); pdf.setTextColor(secondaryColor);
            pdf.text('No inventory items for this room.', margin + 5, yPos);
            yPos += 10;
        } else {
            const tableData = room.items.map((item: InventoryItem) => [
                item.category || 'N/A',
                item.quantity?.toString() || '1', 
                item.condition || 'N/A',
                item.notes || ''
            ]);
            
            const categoryWidth = 35; 
            const quantityWidth = 15; 
            const conditionWidth = 25;
            const notesWidth = pageWidth - (categoryWidth + quantityWidth + conditionWidth) -2; // -2 for small padding

            pdf.autoTable({
                startY: yPos,
                head: [['Category', 'Qty', 'Condition', 'Notes']],
                body: tableData,
                theme: 'plain', 
                headStyles: { 
                    fillColor: [0, 150, 136], // Modern teal color
                    textColor: [255, 255, 255], 
                    fontStyle: 'bold', 
                    halign: 'left', 
                    fontSize: normalSize,
                    cellPadding: { top: 4, bottom: 4, left: 6, right: 6 }
                },
                tableLineColor: [0, 150, 136], // Match header color for border
                tableLineWidth: 0.5,
                columnStyles: { 
                    0: { cellWidth: categoryWidth, halign: 'left', fontStyle: 'bold' }, 
                    1: { cellWidth: quantityWidth, halign: 'center', fontStyle: 'bold' },
                    2: { cellWidth: conditionWidth, halign: 'left', fontStyle: 'bold' },
                    3: { cellWidth: notesWidth, halign: 'left' }
                },
                margin: { left: margin, right: margin }, 
                tableWidth: pageWidth,
                styles: { 
                    cellPadding: { top: 3, bottom: 3, left: 6, right: 6 }, 
                    fontSize: normalSize - 1, 
                    overflow: 'linebreak',
                    lineColor: [220, 220, 220], // Light gray borders
                    lineWidth: 0.1,
                    textColor: [51, 51, 51] // Dark gray text
                },
                alternateRowStyles: { 
                    fillColor: [248, 250, 252] // Very light blue-gray
                },
                didDrawPage: (data: AutoTableHookData) => { 
                    if(data.pageNumber > currentPage) { 
                        currentPage = data.pageNumber; 
                        addHeader(); 
                    }
                    // yPos must be updated based on where autoTable left off on THIS page
                    // For the last page of the table, data.cursor.y is correct.
                    // If a new page was added by autoTable, addHeader resets yPos.
                    yPos = data.cursor?.y ? data.cursor.y + 5 : contentStartY; 
                }
            });
            yPos = (pdf as any).lastAutoTable.finalY + 10 || yPos;
        }
    });

    // Property Photos Section (if applicable, starts on a new page)
    let hasPhotos = rooms.some(room => room?.items?.some((item: InventoryItem) => 
      item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => p && !p.uploading && (p.url || p.localPath))
    ));
    if (hasPhotos) {
        pdf.addPage(); currentPage++; addHeader();
        const photosSectionTitleHeight = 6;
        pdf.setFillColor(secondaryColor); pdf.setDrawColor(secondaryColor);
        pdf.roundedRect(margin, yPos, pageWidth, 6, 2, 2, 'FD');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.setTextColor(255, 255, 255);
        pdf.text('Property Photos', margin + 3, yPos + 4.5);
        yPos += photosSectionTitleHeight + 1; 
        
        let isFirstPhotoCategoryDrawn = false;
        for (const room of rooms) {
             const itemsWithPhotos = room.items.filter((item: InventoryItem) => 
               item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => p && !p.uploading && (p.url || p.localPath))
             );
             if (itemsWithPhotos.length === 0) continue;

             // Group items by category within the room
             const itemsByCategory = itemsWithPhotos.reduce((acc, item) => {
                 const category = item.category || 'Uncategorized';
                 if (!acc[category]) {
                     acc[category] = [];
                 }
                 acc[category].push(item);
                 return acc;
             }, {} as Record<string, InventoryItem[]>);

             // Each category within a room starts on a new page
             for (const [category, categoryItems] of Object.entries(itemsByCategory)) {
                 // Each category starts on a new page (except the very first one)
                 if (isFirstPhotoCategoryDrawn) {
                     pdf.addPage(); currentPage++; addHeader(); 
                     yPos = contentStartY; // Reset yPos for new page
                 } else {
                     // For the first category with photos, ensure adequate space after "Property Photos" title
                     yPos = checkForNewPage(yPos, 60); // Min space for category header and photos
                 }
                 
                 // Draw category header showing both room and category
                 const categoryHeaderHeight = 8;
                 pdf.setFillColor(primaryColor); pdf.setDrawColor(primaryColor);
                 pdf.roundedRect(margin, yPos, pageWidth, categoryHeaderHeight, 2, 2, 'FD');
                 pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(255, 255, 255);
                 pdf.text(`${room.name || 'Unnamed Room'} - ${category}`, margin + 5, yPos + 6);
                 yPos += categoryHeaderHeight + 3; // Reduced space after category header for compact layout
                 isFirstPhotoCategoryDrawn = true;

                 for (const item of categoryItems) {
                 const photos = item.photos?.filter((p: RoomPhoto) => p && !p.uploading && (p.url || p.localPath)) || [];
                  if (photos.length === 0) continue;

                  // Calculate maximum available space for photo grid
                  const availableHeight = pageHeight - footerHeight - yPos - 15; // Reduced buffer
                  const leftMargin = 20; // Reduced left margin for better centering
                  const availableWidth = pageWidth; // Use full width for sizing calculations
                  
                  // *** ADJUSTABLE PHOTO POSITIONING ***
                  // Change this value to fine-tune horizontal positioning:
                  // Positive values move photos RIGHT, negative values move photos LEFT
                  const photoGridHorizontalAdjustment = 15; // Adjust this number to center photos perfectly
                  
                  // Optimize photo dimensions to use maximum space while maintaining 2x2 grid
                  const photoRowSpacing = 6; // Reduced spacing between rows for compact layout
                  const spacingBetweenPhotos = 15; // Space between left/right photos
                  const photoContainerPadding = 4; // Container padding around photos
                  
                  // Calculate max photo dimensions using full page width (maintains original size)
                  const basePhotoWidth = Math.floor((availableWidth - spacingBetweenPhotos - (photoContainerPadding * 4)) / 2);
                  const basePhotoHeight = Math.floor((availableHeight - photoRowSpacing - (photoContainerPadding * 4)) / 2);
                  
                  // Increase dimensions by 20% for better visibility
                  const maxPhotoWidth = Math.floor(basePhotoWidth * 1.2);
                  const maxPhotoHeight = Math.floor(basePhotoHeight * 1.2);
                  
                  const photosPerRow = 2; 
                  const photoLimitPerPage = 4; // 4 photos per page (2x2 grid)
                  const photoBorderRadius = 3;
                  const photoContainerMargin = 0; // No margin - use centering instead

                  // Draw item details without forcing page break (batch processing handles page breaks)
                  yPos = drawPhotoItemDetails(item, yPos);
                  yPos += 2; // Minimal space after compact item details

                  // Process photos in batches of 4 (2x2 grid) for clean layout across rooms
                  for (let batchStart = 0; batchStart < photos.length; batchStart += photoLimitPerPage) {
                      const photoBatch = photos.slice(batchStart, batchStart + photoLimitPerPage);
                      
                      // Only add new page if this batch won't fit on current page and it's not the first batch
                      const batchRequiredSpace = (Math.ceil(photoBatch.length / photosPerRow) * (maxPhotoHeight + photoRowSpacing + (photoContainerPadding * 2))) + 30;
                      if (batchStart > 0 && (yPos + batchRequiredSpace > pageHeight - footerHeight)) {
                          pdf.addPage(); currentPage++; addHeader();
                          yPos = drawPhotoItemDetails(item, yPos); 
                          yPos += 5;
                      }
                      
                      const currentPhotoGridStartY = yPos;
                      
                      for (let i = 0; i < photoBatch.length; i++) {
                          const photo = photoBatch[i];
                          const globalPhotoIndex = batchStart + i; // For numbering
                          
                          const colIndex = i % photosPerRow;
                          const rowIndex = Math.floor(i / photosPerRow);
                          const currentPhotoY = currentPhotoGridStartY + rowIndex * (maxPhotoHeight + photoRowSpacing + (photoContainerPadding * 2));

                          try {
                              // Get the photo URL or local path
                              const photoSrc = photo.url || photo.localPath;
                              if (!photoSrc) {
                                console.warn('Photo has no URL or localPath, skipping');
                                continue;
                              }

                              // Convert URL to base64 for PDF embedding
                              const base64Image = await urlToBase64(photoSrc);
                              
                              // Calculate position for individual photo containers with better spacing
                              const totalContainerWidth = maxPhotoWidth + (photoContainerPadding * 2);
                              const totalWidthNeeded = (photosPerRow * totalContainerWidth);
                              const spacingBetweenContainers = spacingBetweenPhotos;
                              const totalGridWidth = totalWidthNeeded + spacingBetweenContainers;
                              // Center the grid on the page with adjustable horizontal offset
                              const gridStartX = (pageWidth - totalGridWidth) / 2 + photoGridHorizontalAdjustment;
                              
                              // Calculate centered positions for photo grid
                              const containerXPos = gridStartX + colIndex * (totalContainerWidth + spacingBetweenContainers);
                              const photoXPos = containerXPos + photoContainerPadding;
                              const photoYPos = currentPhotoY + photoContainerPadding;
                              
                              // Draw individual photo container background with better styling
                              const containerHeight = maxPhotoHeight + (photoContainerPadding * 2);
                              pdf.setFillColor(250, 250, 252); // Slightly warmer light background
                              pdf.setDrawColor(200, 200, 210); // Subtle border
                              pdf.setLineWidth(0.3);
                              pdf.roundedRect(containerXPos, currentPhotoY, totalContainerWidth, containerHeight, 4, 4, 'FD');
                              
                              // All images are exactly maxPhotoWidth x maxPhotoHeight (uniform sizing)
                              pdf.addImage(base64Image, 'JPEG', photoXPos, photoYPos, maxPhotoWidth, maxPhotoHeight, undefined, 'FAST');
                              
                              // Draw border around the actual photo
                              pdf.setDrawColor(primaryColor); 
                              pdf.setLineWidth(0.3); 
                              pdf.roundedRect(photoXPos, photoYPos, maxPhotoWidth, maxPhotoHeight, photoBorderRadius, photoBorderRadius, 'D');
                              
                              // Photo numbering in top-left corner
                              pdf.setFillColor(primaryColor); 
                              pdf.setDrawColor(primaryColor);
                              pdf.roundedRect(photoXPos + 2, photoYPos + 2, 10, 8, 1.5, 1.5, 'FD');
                              pdf.setFont('helvetica', 'bold'); 
                              pdf.setFontSize(smallSize); 
                              pdf.setTextColor(255, 255, 255);
                              pdf.text(`${globalPhotoIndex + 1}`, photoXPos + 7, photoYPos + 7, { align: 'center' });
                              
                          } catch (imgError) { 
                              console.error('Error adding photo to PDF:', imgError);
                              // Draw error placeholder in the same position
                              const totalContainerWidth = maxPhotoWidth + (photoContainerPadding * 2);
                              const totalWidthNeeded = (photosPerRow * totalContainerWidth);
                              const spacingBetweenContainers = spacingBetweenPhotos;
                              const totalGridWidth = totalWidthNeeded + spacingBetweenContainers;
                              const gridStartX = (pageWidth - totalGridWidth) / 2 + photoGridHorizontalAdjustment;
                              
                              const containerXPos = gridStartX + colIndex * (totalContainerWidth + spacingBetweenContainers);
                              const photoXPos = containerXPos + photoContainerPadding;
                              const photoYPos = currentPhotoY + photoContainerPadding;
                              
                              // Draw error placeholder container
                              const containerHeight = maxPhotoHeight + (photoContainerPadding * 2);
                              pdf.setFillColor(250, 250, 252);
                              pdf.setDrawColor(200, 200, 210);
                              pdf.setLineWidth(0.3);
                              pdf.roundedRect(containerXPos, currentPhotoY, totalContainerWidth, containerHeight, 4, 4, 'FD');
                            
                              // Draw error placeholder
                              pdf.setFillColor(240, 240, 240);
                              pdf.roundedRect(photoXPos, photoYPos, maxPhotoWidth, maxPhotoHeight, photoBorderRadius, photoBorderRadius, 'F');
                              pdf.setDrawColor(primaryColor); 
                              pdf.setLineWidth(0.3);
                              pdf.roundedRect(photoXPos, photoYPos, maxPhotoWidth, maxPhotoHeight, photoBorderRadius, photoBorderRadius, 'D');
                              
                              // Add "Image Error" text
                              pdf.setFont('helvetica', 'normal'); 
                              pdf.setFontSize(smallSize + 1); 
                              pdf.setTextColor(100, 100, 100);
                              pdf.text('Image Error', photoXPos + maxPhotoWidth/2, photoYPos + maxPhotoHeight/2, { align: 'center' });
                          }
                      }
                      
                      // Update yPos to after this batch - ensure proper spacing for next batch or item
                      const rowsInBatch = Math.ceil(photoBatch.length / photosPerRow);
                      yPos = currentPhotoGridStartY + (rowsInBatch * (maxPhotoHeight + photoRowSpacing + (photoContainerPadding * 2))) + 15;
                  }
                 } // Close item loop
             } // Close category loop
         } // Close room loop
    }

    // Last Page: Contact Information, Disclaimer, Final Signatures
    pdf.addPage(); currentPage++; addHeader(); 

    // Only show contact information if company details are provided (not standard plan)
    const hasContactInfo = companyName || companyWebsite || companyPhone || companyEmail || companyAddress;
    
    if (hasContactInfo) {
      yPos = checkForNewPage(yPos, 70); 
      pdf.setFillColor(lightGray);
      pdf.setDrawColor(primaryColor);
      pdf.roundedRect(margin, yPos, pageWidth, 60, 3, 3, 'FD'); 
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(subheadingSize);
      pdf.setTextColor(secondaryColor);
      pdf.text('Contact Information', margin + 5, yPos + 8);
      pdf.setFontSize(normalSize);
      pdf.setTextColor(textColor);
      let contactY = yPos + 18;
      
      if (companyName) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(companyName, margin + 5, contactY); 
        contactY += 6;
        pdf.setFont('helvetica', 'normal');
      }
      
      if (companyAddress) {
        // Split address into lines
        const addressLines: string[] = companyAddress.split(',').map((line: string) => line.trim());
        addressLines.forEach((line: string) => {
          pdf.text(line, margin + 5, contactY);
          contactY += 6;
        });
        contactY += 2; // Extra spacing after address
      }
      
      if (companyWebsite) {
        pdf.text(`Website: ${companyWebsite}`, margin + 5, contactY);
        contactY += 6;
      }
      
      if (companyEmail) {
        pdf.text(`Email: ${companyEmail}`, margin + 5, contactY);
        contactY += 6;
      }
      
      if (companyPhone) {
        pdf.text(`Phone: ${companyPhone}`, margin + 5, contactY);
        contactY += 8;
      }
      
      pdf.setFontSize(smallSize);
      pdf.text('For any questions or clarifications regarding this document,', margin + 5, contactY);
      contactY += 4;
      pdf.text('please contact us using the details above.', margin + 5, contactY);
      yPos += 65;
    } 

    yPos = checkForNewPage(yPos, 40); 
    pdf.setFillColor(lightGray);
    pdf.setDrawColor(primaryColor);
    pdf.roundedRect(margin, yPos, pageWidth, 35, 3, 3, 'FD'); 
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(subheadingSize);
    pdf.setTextColor(secondaryColor);
    pdf.text('Disclaimer', margin + 5, yPos + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(smallSize);
    pdf.setTextColor(textColor);
    const disclaimerText = "By signing this document, you agree that all items in this inventory have been inspected and the condition recorded reflects the state at the time of check-in. Any alterations or omissions must be reported within 7 days of taking occupancy or this inventory will be accepted as an accurate record.";
    const splitDisclaimer = pdf.splitTextToSize(disclaimerText, pageWidth - 10); 
    pdf.text(splitDisclaimer, margin + 5, yPos + 15);
    yPos += 40; 

    yPos = checkForNewPage(yPos, 70); 
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(headingSize); pdf.setTextColor(secondaryColor);
    pdf.text('Final Signatures', margin, yPos);
    pdf.setDrawColor(primaryColor); pdf.setLineWidth(0.5);
    pdf.line(margin, yPos + 5, margin + 50, yPos + 5);
    yPos += 15; 

    const sigWidth = 70; const sigHeight = 25; 
    const sigLabelYOffset = sigHeight + 8;

    yPos = checkForNewPage(yPos, sigHeight + 15);
    if (signatures.assessor && signatures.assessor.startsWith('data:image/png')) {
        try { pdf.addImage(signatures.assessor, 'PNG', margin, yPos, sigWidth, sigHeight); }
        catch (imgError) { console.error("Error adding assessor signature image:", imgError); }
    }
    pdf.setDrawColor(textColor); pdf.setLineWidth(0.3);
    pdf.line(margin, yPos + sigHeight, margin + sigWidth, yPos + sigHeight); 
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(smallSize); pdf.setTextColor(textColor);
    pdf.text('Assessor Signature', margin, yPos + sigLabelYOffset);
    yPos += sigHeight + 15; 

    yPos = checkForNewPage(yPos, sigHeight + 15);
     if (signatures.customer && signatures.customer.startsWith('data:image/png')) {
         try { pdf.addImage(signatures.customer, 'PNG', margin, yPos, sigWidth, sigHeight); }
         catch (imgError) { console.error("Error adding customer signature image:", imgError); }
     }
    pdf.line(margin, yPos + sigHeight, margin + sigWidth, yPos + sigHeight); 
    pdf.text('Customer Signature', margin, yPos + sigLabelYOffset);
    // yPos += sigHeight + 15; // No more content after this on this page

    // --- Finalize PDF ---
    // Add page numbers to all existing headers 
    totalPages = pdf.getNumberOfPages(); 
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      // Only add page number to existing header (don't redraw entire header)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(smallSize);
      pdf.setTextColor(secondaryColor);
      pdf.text(`Page ${i} of ${totalPages}`, 210 - margin, margin + 8, { align: 'right' });
    }
    
    const pdfBlob = pdf.output('blob');

    if (typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isNativePlatform()) {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');

        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);

        reader.onloadend = async function() {
          const base64data = reader.result as string;
          const base64String = base64data.split(',')[1];
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
          const fileName = `Property_Inventory_${formattedDate}.pdf`;

          try {
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64String,
              directory: Directory.Cache, // Use Cache for temporary shareable file
              recursive: true
            });

            await Share.share({
              title: 'Property Inventory Report',
              text: 'Here is your property inventory report',
              url: result.uri, // Share the file URI
              dialogTitle: 'Share or Save PDF Report'
            });
          } catch (fsError) {
            console.error('Filesystem/Share error:', fsError);
            // Fallback or specific error handling
             try { // Attempt direct base64 share as fallback (might not work everywhere)
                await Share.share({
                  title: 'Property Inventory Report',
                  text: 'Here is your property inventory report',
                  url: base64data, // Use base64 data URI
                  dialogTitle: 'Share or Save PDF Report'
                });
              } catch (shareError) {
                console.error('Fallback Share error:', shareError);
                alert('Unable to share the PDF. Please try saving manually.');
              }
          }
        };
      } catch (error) {
        console.error('Error loading Capacitor plugins:', error);
        alert('Error exporting PDF on mobile. Please ensure Capacitor is properly configured.');
      }
    } else {
      // Web browser download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      link.download = `Property_Inventory_${formattedDate}.pdf`;
      document.body.appendChild(link); // Append link to body for Firefox compatibility
      link.click();
      document.body.removeChild(link); // Clean up link
      URL.revokeObjectURL(url); // Clean up blob URL
    }
  } catch (error: any) {
    console.error('Error generating PDF:', error);
    alert(`Error generating PDF. Please check console for details. Message: ${error?.message || 'Unknown error'}`);
  }
}; 